import React, { useState, useEffect } from 'react';
import { TextInput, Button, Box, Textarea, Notification, Grid, Col, Text, Title } from '@mantine/core';
import { IconAt, IconCircleCheck } from '@tabler/icons-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import HoneypotField from '../../../common/components/HoneypotField';
import WafCaptchaModal from '../../../common/components/WafCaptchaModal';
import { ensureWafSdk, isWafChallengeStatus, wafTokenHeader } from '../../../common/utils/wafToken';

const COOLDOWN_SECONDS = 30;

const GetStarted = () => {
    const [formData, setFormData] = useState({
        farmName: '',
        contactName: '',
        email: '',
        phone: '',
        location: '',
        herdSize: '',
        referral: '',
        notes: '',
        // Bot trap - never rendered to a person. See common/components/HoneypotField.
        website: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [captchaOpen, setCaptchaOpen] = useState(false);

    useEffect(() => {
        if (countdown <= 0) return undefined;
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // Load the WAF SDK with the form, not in index.html: the landing pages must not pay for a script
    // they never use, and loading it here gives it the whole time the person spends typing to acquire
    // a token in the background.
    useEffect(() => {
        void ensureWafSdk();
    }, []);

    const handleChange = (name: string, value: string | undefined) => {
        setFormData((prev) => ({ ...prev, [name]: value ?? '' }));
    };

    const submitLead = async (isRetry: boolean) => {
        setLoading(true);
        setError('');

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const response = await fetch(`${apiUrl}/api/leads`, {
                method: 'POST',
                // A missing token is not an error: the header is simply absent and WAF decides.
                headers: { 'Content-Type': 'application/json', ...(await wafTokenHeader()) },
                body: JSON.stringify({
                    organizationName: formData.farmName,
                    contactName: formData.contactName,
                    email: formData.email,
                    phone: formData.phone || undefined,
                    location: formData.location || undefined,
                    herdSize: Number.isNaN(parseInt(formData.herdSize, 10))
                        ? undefined
                        : parseInt(formData.herdSize, 10),
                    referral: formData.referral || undefined,
                    notes: formData.notes || undefined,
                    website: formData.website,
                }),
            });

            // WAF answers a request it will not let through with 202/405, before linok-api ever sees
            // it. Checked ahead of `response.ok` because 202 is itself an ok status.
            if (isWafChallengeStatus(response.status)) {
                if (isRetry) {
                    setError('We could not verify your browser. Please try again.');
                } else {
                    setCaptchaOpen(true);
                }
                return;
            }

            if (response.ok) {
                setCountdown(COOLDOWN_SECONDS);
                setFormData({
                    farmName: '',
                    contactName: '',
                    email: '',
                    phone: '',
                    location: '',
                    herdSize: '',
                    referral: '',
                    notes: '',
                    website: '',
                });
            } else {
                const data = await response.json();
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void submitLead(false);
    };

    // The SDK caches the token minted by a solved puzzle, so re-submitting picks it up. One retry only.
    const handleCaptchaSolved = () => {
        setCaptchaOpen(false);
        void submitLead(true);
    };

    const fieldStyle = {
        marginBottom: '1rem',
    };

    const inputStyles = {
        input: {
            backgroundColor: '#f1f3f5',
            border: '1px solid #ced4da',
            '&:focus': {
                borderColor: '#156b83',
                backgroundColor: '#fff',
            },
        },
        label: {
            fontWeight: 600,
            color: '#0F2137',
            marginBottom: '0.35rem',
        },
    };

    if (countdown > 0) {
        return (
            <Box style={{
                maxWidth: 800,
                margin: 'auto',
                padding: '3rem 1.5rem',
                borderRadius: '8px',
                backgroundColor: 'white',
                textAlign: 'center',
            }}>
                <IconCircleCheck
                    size={80}
                    style={{ color: '#156b83', marginBottom: '1.5rem' }}
                    stroke={1.5}
                />
                <Title order={2} style={{ color: '#0F2137', marginBottom: '0.75rem' }}>
                    We received your request!
                </Title>
                <Text size="lg" color="dimmed" style={{ maxWidth: 480, margin: '0 auto 2rem' }}>
                    Thank you for your interest in Linok. A member of our team will reach out to you shortly to discuss how we can help transform your dairy operation.
                </Text>
                <Text size="sm" color="dimmed">
                    You can submit another request in {countdown} seconds
                </Text>
            </Box>
        );
    }

    return (
        <Box style={{ maxWidth: 800, margin: 'auto', padding: '1.5rem', borderRadius: '8px', backgroundColor: 'white' }}>
            <style>{`
                .linok-phone-input .PhoneInputInput {
                    background-color: #f1f3f5;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    padding: 0.5rem 0.75rem;
                    font-size: 16px;
                    height: 42px;
                    outline: none;
                    transition: border-color 0.15s ease, background-color 0.15s ease;
                }
                .linok-phone-input .PhoneInputInput:focus {
                    border-color: #156b83;
                    background-color: #fff;
                }
            `}</style>
            <WafCaptchaModal
                opened={captchaOpen}
                onClose={() => setCaptchaOpen(false)}
                onSolved={handleCaptchaSolved}
            />
            <form onSubmit={handleSubmit}>
                <HoneypotField value={formData.website} onChange={(v) => handleChange('website', v)} />
                <Grid gutter="md">
                    <Col xs={12} sm={6}>
                        <TextInput
                            size="md"
                            label="Farm / Business name"
                            placeholder="Farm / Business name"
                            value={formData.farmName}
                            onChange={(e) => handleChange('farmName', e.target.value)}
                            required
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <TextInput
                            size="md"
                            label="Contact name"
                            placeholder="Contact name"
                            value={formData.contactName}
                            onChange={(e) => handleChange('contactName', e.target.value)}
                            required
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <TextInput
                            size="md"
                            label="Email address"
                            icon={<IconAt size={16} />}
                            placeholder="Email address"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <div style={fieldStyle}>
                            <Text size="sm" weight={600} style={{ color: '#0F2137', marginBottom: '0.35rem' }}>
                                Phone number (optional)
                            </Text>
                            <PhoneInput
                                defaultCountry="US"
                                placeholder="Phone number"
                                value={formData.phone}
                                className="linok-phone-input"
                                onChange={(value) => handleChange('phone', value)}
                            />
                        </div>
                    </Col>
                    <Col xs={12} sm={6}>
                        <TextInput
                            size="md"
                            label="Location (optional)"
                            placeholder="City, state"
                            value={formData.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <TextInput
                            size="md"
                            label="Herd size (optional)"
                            placeholder="Number of cows"
                            type="number"
                            min={0}
                            value={formData.herdSize}
                            onChange={(e) => handleChange('herdSize', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <TextInput
                            size="md"
                            label="How did you hear about us? (optional)"
                            placeholder="A friend, Google, social media..."
                            value={formData.referral}
                            onChange={(e) => handleChange('referral', e.target.value)}
                            style={fieldStyle}
                            styles={inputStyles}
                        />

                        <Textarea
                            size="md"
                            label="Notes (optional)"
                            placeholder="Anything else you'd like us to know?"
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            minRows={4}
                            style={{ ...fieldStyle, height: 'auto' }}
                            styles={inputStyles}
                        />
                    </Col>
                </Grid>

                {error && (
                    <Notification color="red" onClose={() => setError('')} style={{ marginBottom: '1rem' }}>
                        {error}
                    </Notification>
                )}

                <Button type="submit" fullWidth loading={loading} style={{ marginTop: '1rem' }}>
                    {loading ? 'Submitting...' : '🐄 Request a Demo'}
                </Button>
            </form>
        </Box>
    );
};

export default GetStarted;
